import { useState, useRef, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { Button, Flex, Input, Modal, Popover } from "antd"
import { FileOutlined, DeleteOutlined, MoreOutlined, PaperClipOutlined } from "@ant-design/icons"
import { AlertIcon } from "ui/Icon/DSIcon/AlertIcon"
import { Text } from "ui/Typography"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import CustomDrawer from "ui/Drawer"
import { createExperienceNote, updateExperienceNote, deleteExperienceNote, selectNoteMutationLoading } from "store/upcomingExperiences"
import styles from "./NotesSection.module.css"
import { spacings } from "themes/variables"
import { MAX_VISIBLE_IMAGES, NOTE_COLLAPSE_CHARS } from "./constants"

const fileType = (name) => {
  const ext = name.split(".").pop().toLowerCase()
  if (["jpg", "jpeg", "png", "gif", "webp", "heic"].includes(ext)) return "image"
  if (["mp4", "mov", "avi", "webm"].includes(ext)) return "video"
  return "file"
}

const readFileAsDataUrl = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target.result)
    reader.readAsDataURL(file)
  })

const AttachmentThumbnail = ({ attachment }) => {
  if (attachment.type === "image" && attachment.dataUrl) {
    return <img src={attachment.dataUrl} alt={attachment.name} className={styles.thumbImage} />
  }
  return (
    <div className={styles.thumbFile}>
      <FileOutlined className={styles.thumbFileIcon} />
    </div>
  )
}

AttachmentThumbnail.propTypes = {
  attachment: PropTypes.shape({
    type: PropTypes.string.isRequired,
    dataUrl: PropTypes.string,
    name: PropTypes.string.isRequired
  }).isRequired
}

const NoteCard = ({ note, onEdit, onDelete }) => {
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm"].includes(breakpoint)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const images = note.attachments.filter((a) => a.type === "image")
  const files = note.attachments.filter((a) => a.type !== "image")
  const totalAttachments = note.attachments.length

  const visibleImages = images.slice(0, MAX_VISIBLE_IMAGES)
  const hiddenImageCount = images.length - MAX_VISIBLE_IMAGES

  const firstFile = files[0] ?? null
  const hiddenFileCount = files.length - 1

  const isLong = note.text.length > NOTE_COLLAPSE_CHARS
  const displayText = isLong && !isExpanded ? note.text.slice(0, NOTE_COLLAPSE_CHARS) + "…" : note.text

  const menuContent = (
    <div className={styles.menuList}>
      <button
        type="button"
        className={styles.menuItem}
        onClick={() => {
          setIsMenuOpen(false)
          onEdit(note)
        }}
      >
        Edit Note
      </button>
      <button
        type="button"
        className={`${styles.menuItem} ${styles.menuItemDelete}`}
        onClick={() => {
          setIsMenuOpen(false)
          onDelete(note)
        }}
      >
        Delete Note
      </button>
    </div>
  )

  return (
    <div className={styles.noteCard}>
      <Flex align="flex-start" justify="space-between" gap={4}>
        <Flex gap={spacings.XS} align="center">
          <span className={styles.noteAuthor}>{note.author}</span>
          <span className={styles.noteDate}>{note.date}</span>
        </Flex>
        {isMobile ? (
          <button type="button" className={styles.moreBtn} onClick={() => setIsMenuOpen(true)}>
            <MoreOutlined />
          </button>
        ) : (
          <Popover content={menuContent} trigger="click" placement="bottomRight" open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <button type="button" className={styles.moreBtn}>
              <MoreOutlined />
            </button>
          </Popover>
        )}
      </Flex>

      {/* Mobile: note actions in a bottom-sheet drawer instead of the popover */}
      {isMobile && (
        <CustomDrawer open={isMenuOpen} setOpen={setIsMenuOpen} title="Actions">
          <Flex vertical gap={12} className={styles.actionsSheet}>
            <Button
              block
              shape="round"
              color="primary"
              variant="outlined"
              onClick={() => {
                setIsMenuOpen(false)
                onEdit(note)
              }}
            >
              Edit Note
            </Button>
            <Button
              block
              shape="round"
              danger
              onClick={() => {
                setIsMenuOpen(false)
                onDelete(note)
              }}
            >
              Delete Note
            </Button>
          </Flex>
        </CustomDrawer>
      )}

      <p className={styles.noteText}>{displayText}</p>

      {totalAttachments > 0 && (
        <>
          {images.length > 0 && (
            <Flex align="center" gap={4} className={styles.imageRow}>
              {visibleImages.map((img) => (
                <div key={img.id} className={styles.imageWrap}>
                  <AttachmentThumbnail attachment={img} />
                </div>
              ))}
              {hiddenImageCount > 0 && (
                <Flex flex={1} justify="center" align="center" className={styles.moreImagesBadge}>
                  +{hiddenImageCount}
                </Flex>
              )}
            </Flex>
          )}

          {firstFile && (
            <Flex align="center" gap={4}>
              <Flex flex={4} className={styles.fileRow}>
                <FileOutlined className={styles.fileRowIcon} />
                <span className={styles.fileRowName}>{firstFile.name}</span>
              </Flex>
              {hiddenFileCount > 0 && (
                <Flex flex={1} align="center" justify="center" className={styles.moreFilesBadge}>
                  +{hiddenFileCount}
                </Flex>
              )}
            </Flex>
          )}

          <Flex align="center" gap={8} className={styles.noteFooter}>
            {isLong && (
              <button type="button" className={styles.toggleTextBtn} onClick={() => setIsExpanded((v) => !v)}>
                {isExpanded ? "∧ Show less" : "∨ Show more"}
              </button>
            )}
            <span className={styles.attachmentCount}>
              <PaperClipOutlined className={styles.attachmentCountIcon} />
              {totalAttachments} attachments
            </span>
          </Flex>
        </>
      )}

      {totalAttachments === 0 && isLong && (
        <button type="button" className={styles.toggleTextBtn} onClick={() => setIsExpanded((v) => !v)}>
          {isExpanded ? "∧ Show less" : "∨ Show more"}
        </button>
      )}
    </div>
  )
}

NoteCard.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    author: PropTypes.string,
    date: PropTypes.string,
    text: PropTypes.string,
    attachments: PropTypes.array.isRequired
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}

const AddNoteModal = ({ open, initial, onClose, onSave, saving = false }) => {
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm"].includes(breakpoint)
  const [text, setText] = useState("")
  const [attachments, setAttachments] = useState([])
  const [uploading, setUploading] = useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (open) {
      setText(initial?.text ?? "")
      setAttachments(initial?.attachments ?? [])
      setUploading(null)
    }
  }, [open, initial])

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList)
    for (const file of files) {
      const type = fileType(file.name)
      const tempId = `${Date.now()}_${file.name}`
      setUploading(tempId)
      const dataUrl = type === "image" ? await readFileAsDataUrl(file) : null
      setAttachments((prev) => [...prev, { id: tempId, name: file.name, type, dataUrl, file }])
      setUploading(null)
    }
  }, [])

  const handleRemoveAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }

  const handleSave = () => {
    if (!text.trim()) return
    onSave({ text: text.trim(), attachments })
  }

  const body = (
    <Flex vertical gap={12}>
      <Flex vertical gap={4}>
        <span className={styles.modalLabel}>Notes</span>
        <Input.TextArea
          placeholder="Enter notes"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoSize={{ minRows: 3, maxRows: 6 }}
        />
      </Flex>

      <Flex vertical gap={8}>
        <span className={styles.modalLabel}>Add photos, videos or documents.</span>
        <Button
          icon={<PaperClipOutlined />}
          onClick={() => fileInputRef.current?.click()}
          color="primary"
          variant="outlined"
          shape="round"
          className={styles.attachBtn}
        >
          Attach Files
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
          className={styles.hiddenInput}
          onChange={(e) => {
            handleFiles(e.target.files)
            e.target.value = ""
          }}
        />

        {attachments.map((att) => (
          <div key={att.id} className={styles.modalFileRow}>
            <FileOutlined className={styles.fileRowIcon} />
            <span className={styles.fileRowName}>{att.name}</span>
            <button type="button" className={styles.fileRemoveBtn} onClick={() => handleRemoveAttachment(att.id)}>
              <DeleteOutlined />
            </button>
          </div>
        ))}

        {uploading && (
          <div className={styles.modalFileRow}>
            <FileOutlined className={styles.fileRowIcon} />
            <span className={styles.fileRowName}>Uploading…</span>
            <div className={styles.uploadProgressWrap}>
              <div className={styles.uploadProgressBar} />
              <span className={styles.uploadProgressLabel}>25% done</span>
            </div>
          </div>
        )}
      </Flex>
    </Flex>
  )

  const cancelBtn = (
    <Button onClick={onClose} disabled={saving}>Cancel</Button>
  )
  const saveBtn = (
    <Button type="primary" onClick={handleSave} disabled={!text.trim() || saving} loading={saving}>
      Save
    </Button>
  )

  // Mobile: render as a bottom-sheet drawer with a full-width footer
  if (isMobile) {
    return (
      <CustomDrawer open={open} setOpen={(next) => !next && onClose()} title="Add Note">
        <Flex vertical gap={16} className={styles.addNoteDrawerBody}>
          {body}
          <Flex gap={8} className={styles.addNoteDrawerFooter}>
            <Button block shape="round" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button block shape="round" type="primary" onClick={handleSave} disabled={!text.trim() || saving} loading={saving}>
              Save
            </Button>
          </Flex>
        </Flex>
      </CustomDrawer>
    )
  }

  return (
    <Modal
      open={open}
      title="Add Note"
      onCancel={onClose}
      footer={
        <Flex gap={8} justify="flex-start">
          {cancelBtn}
          {saveBtn}
        </Flex>
      }
      width={480}
      destroyOnClose
    >
      {body}
    </Modal>
  )
}

AddNoteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  initial: PropTypes.shape({
    text: PropTypes.string,
    attachments: PropTypes.array
  }),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool
}

const DeleteNoteModal = ({ open, onClose, onConfirm, deleting = false }) => (
  <Modal open={open} onCancel={onClose} footer={null} width={400} closable centered>
    <Flex vertical align="flex-start" gap={12} className={styles.deleteModalBody}>
      <div className={styles.deleteIcon}>
        <AlertIcon width={24} height={24} />
      </div>
      <Flex vertical gap={4}>
        <span className={styles.deleteTitle}>Delete Note</span>
        <span className={styles.deleteSubtitle}>Are you sure you want to delete note?</span>
      </Flex>
      <Flex gap={8} className={styles.deleteModalActions}>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button danger type="primary" onClick={onConfirm} loading={deleting}>
          Delete
        </Button>
      </Flex>
    </Flex>
  </Modal>
)

DeleteNoteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  deleting: PropTypes.bool
}

// Backend attachment ({ id, filename, content_type, url }) → display shape
const mapAttachment = (att) => {
  const isImage = (att.content_type || "").startsWith("image/")
  return {
    id: att.id,
    name: att.filename || att.name || "",
    type: isImage ? "image" : "file",
    dataUrl: isImage ? att.url : null,
    url: att.url || null,
    uploaded: true
  }
}

// Backend note ({ id, content, created_at, author: { name, type }, attachments }) → display shape
const mapNote = (note) => ({
  id: note.id,
  author: note.author?.name || "",
  authorType: note.author?.type || "",
  date: note.created_at ? new Date(note.created_at).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "",
  text: note.content ?? "",
  attachments: Array.isArray(note.attachments) ? note.attachments.map(mapAttachment) : []
})

const NotesSection = ({ bookingId = "default", notes: initialNotes = [], hideTitle = false }) => {
  const dispatch = useDispatch()
  const mutationLoading = useSelector(selectNoteMutationLoading)
  const [notes, setNotes] = useState(() => initialNotes.map(mapNote))
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [editNote, setEditNote] = useState(null)
  const [deleteNote, setDeleteNote] = useState(null)

  const handleSave = async ({ text, attachments }) => {
    // PATCH/POST append new files; existing (already-uploaded) attachments carry no File.
    const files = attachments.filter((att) => att.file).map((att) => att.file)
    try {
      if (editNote) {
        const res = await dispatch(updateExperienceNote(bookingId, editNote.id, { content: text, files }))
        const updated = mapNote(res.data)
        setNotes((prev) => prev.map((note) => (note.id === editNote.id ? updated : note)))
        setEditNote(null)
      } else {
        const res = await dispatch(createExperienceNote(bookingId, { content: text, files }))
        setNotes((prev) => [mapNote(res.data), ...prev])
      }
      setIsAddOpen(false)
    } catch {
      // success toast + error toast are handled by the store/api helper
    }
  }

  const handleEdit = (note) => {
    setEditNote(note)
    setIsAddOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteExperienceNote(bookingId, deleteNote.id))
      setNotes((prev) => prev.filter((note) => note.id !== deleteNote.id))
      setDeleteNote(null)
    } catch {
      // success toast + error toast are handled by the store/api helper
    }
  }

  return (
    <>
      <Flex align="center" justify={hideTitle ? "flex-end" : "space-between"} className={styles.notesHeader}>
        {!hideTitle && <Text className={styles.sectionTitle}>Notes</Text>}
        <Button
          color="primary"
          variant="outlined"
          size="small"
          className={styles.addNoteBtn}
          onClick={() => {
            setEditNote(null)
            setIsAddOpen(true)
          }}
        >
          Add note
        </Button>
      </Flex>

      {notes.length === 0 ? (
        <Text className={styles.notesEmpty}>No notes added.</Text>
      ) : (
        <Flex vertical gap={0}>
          {notes.map((note) => (
            <NoteCard key={note.id} note={note} onEdit={handleEdit} onDelete={setDeleteNote} />
          ))}
        </Flex>
      )}

      <AddNoteModal
        open={isAddOpen}
        initial={editNote}
        saving={mutationLoading}
        onClose={() => {
          setIsAddOpen(false)
          setEditNote(null)
        }}
        onSave={handleSave}
      />

      <DeleteNoteModal open={!!deleteNote} deleting={mutationLoading} onClose={() => setDeleteNote(null)} onConfirm={handleDeleteConfirm} />
    </>
  )
}

NotesSection.propTypes = {
  bookingId: PropTypes.string,
  notes: PropTypes.array,
  hideTitle: PropTypes.bool
}

export default NotesSection
